import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import classroomService from '../../../services/classroomService';
import Loading from '../../../components/Loading';
import { IntervalSelector, SectionCard, enrichResources } from './ClassroomShared';
import sanitizeHtml from '../../../utils/sanitizeHtml';
import '../AulaVirtual.css';

// Vista del estudiante: intervalos visibles -> secciones visibles ->
// recursos visibles. Selector de intervalos arriba, secciones del
// intervalo elegido abajo (mismo layout que usa el docente en
// ClassroomManage, sin los botones de administración).
const ClassroomView = () => {
    const { classroomId } = useParams();
    const [classroom, setClassroom] = useState(null);
    const [intervals, setIntervals] = useState([]);
    const [seleccionado, setSeleccionado] = useState(null);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingSections, setLoadingSections] = useState(false);

    useEffect(() => {
        const cargar = async () => {
            try {
                const cl = await classroomService.getClassroom(classroomId);
                setClassroom(cl);
                const ivs = await classroomService.getIntervals(classroomId, 'student');
                const ordenados = (ivs || []).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
                setIntervals(ordenados);
                if (ordenados.length > 0) setSeleccionado(ordenados[0]);
            } catch (error) {
                console.error(error);
            }
            setLoading(false);
        };
        cargar();
    }, [classroomId]);

    useEffect(() => {
        const cargarSecciones = async () => {
            if (!seleccionado) {
                setSections([]);
                return;
            }
            setLoadingSections(true);
            try {
                const secs = await classroomService.getSections(seleccionado.intervalId, 'student');
                const secsConRecursos = await Promise.all(
                    (secs || []).map(async (sec) => ({ ...sec, resources: await enrichResources(await classroomService.getResources(sec.sectionId, 'student')) }))
                );
                setSections(secsConRecursos.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)));
            } catch (error) {
                console.error(error);
            }
            setLoadingSections(false);
        };
        cargarSecciones();
    }, [seleccionado]);

    if (loading) return <Loading />;

    if (!classroom || !classroom.classroomId) {
        return <div className="alert alert-warning av-module">No encontramos esta aula.</div>;
    }

    return (
        <div className="container-fluid p-0 av-module">
            <div className="mb-4">
                <h1 className="h3 mb-0">{classroom.schoolSubjectName}</h1>
                <p className="text-muted mb-0">{classroom.courseName} · Docente: {classroom.teacherName}</p>
            </div>

            {classroom.welcomeMessageHtml && (
                <div className="alert alert-primary" dangerouslySetInnerHTML={{ __html: sanitizeHtml(classroom.welcomeMessageHtml) }} />
            )}

            {intervals.length === 0 ? (
                <div className="alert alert-info">El docente todavía no ha publicado contenido en esta aula.</div>
            ) : (
                <>
                    <IntervalSelector intervals={intervals} seleccionado={seleccionado} onSelect={setSeleccionado} />

                    {loadingSections ? (
                        <Loading />
                    ) : sections.length === 0 ? (
                        <div className="alert alert-info">Este intervalo todavía no tiene contenido visible.</div>
                    ) : (
                        sections.map((section) => <SectionCard section={section} key={section.sectionId} />)
                    )}
                </>
            )}
        </div>
    );
};

export default ClassroomView;
